import { find, remove } from 'lodash-es'
import { defineStore } from 'pinia'

import { useOAuthStore } from 'src/stores/oauth.js'

import {
  addAccountsToList,
  createList,
  deleteList,
  fetchLists,
  getList,
  getListAccounts,
  removeAccountsFromList,
  updateList,
} from 'src/api/user.js'
import { pollingActions } from 'src/services/poller/poller.js'

export const useListsStore = defineStore('lists', {
  state: () => ({
    poller: null,
    allLists: [],
    allListsObject: {},
  }),
  getters: {
    findListTitle() {
      return (id) => {
        if (!this.allListsObject[id]) return
        return this.allListsObject[id].title
      }
    },
    findListExclusive() {
      return (id) => !!this.allListsObject[id]?.exclusive
    },
    findListAccounts() {
      return (id) => [...this.allListsObject[id].accountIds]
    },
  },
  actions: {
    // every four minutes
    ...pollingActions((store) => store.loadLists(), 240000),
    loadLists() {
      return fetchLists({ credentials: useOAuthStore().token }).then(
        ({ data: lists }) => this.setLists(lists),
      )
    },
    setLists(value) {
      this.allLists = value
      this.allListsObject = Object.fromEntries(
        value.map((list) => [list.id, list]),
      )
    },
    async createList({ title, exclusive }) {
      return await createList({
        title,
        exclusive,
        credentials: useOAuthStore().token,
      }).then(({ data: list }) => {
        this.rememberList({ listId: list.id, title, exclusive: list.exclusive })
        return list
      })
    },
    async fetchList({ listId }) {
      return await getList({
        listId,
        credentials: useOAuthStore().token,
      }).then(({ data: list }) =>
        this.rememberList({
          listId: list.id,
          title: list.title,
          exclusive: list.exclusive,
        }),
      )
    },
    // what the server says about a list, kept here without saving it back
    rememberList({ listId, title, exclusive }) {
      if (!this.allListsObject[listId]) {
        this.allListsObject[listId] = { accountIds: [] }
      }
      this.allListsObject[listId].title = title
      this.allListsObject[listId].exclusive = !!exclusive

      const entry = find(this.allLists, { id: listId })
      if (!entry) {
        this.allLists.push({ id: listId, title, exclusive: !!exclusive })
      } else {
        entry.title = title
        entry.exclusive = !!exclusive
      }
    },
    async fetchListAccounts({ listId }) {
      return await getListAccounts({
        listId,
        credentials: useOAuthStore().token,
      }).then(({ data: accountIds }) => {
        if (!this.allListsObject[listId]) {
          this.allListsObject[listId] = { accountIds: [] }
        }
        this.allListsObject[listId].accountIds = accountIds
      })
    },
    async setList({ listId, title, exclusive }) {
      await updateList({
        listId,
        title,
        exclusive,
        credentials: useOAuthStore().token,
      })
      this.rememberList({
        listId,
        title,
        exclusive: exclusive ?? this.allListsObject[listId]?.exclusive,
      })
    },
    async setListAccounts({ listId, accountIds }) {
      const saved = this.allListsObject[listId]?.accountIds || []
      const added = accountIds.filter((id) => !saved.includes(id))
      const removed = saved.filter((id) => !accountIds.includes(id))
      if (!this.allListsObject[listId]) {
        this.allListsObject[listId] = { accountIds: [] }
      }
      this.allListsObject[listId].accountIds = accountIds
      const promises = []
      if (added.length > 0) {
        promises.push(
          addAccountsToList({
            listId,
            accountIds: added,
            credentials: useOAuthStore().token,
          }),
        )
      }
      if (removed.length > 0) {
        promises.push(
          removeAccountsFromList({
            listId,
            accountIds: removed,
            credentials: useOAuthStore().token,
          }),
        )
      }
      await Promise.all(promises)
    },
    async addListAccount({ listId, accountId }) {
      return await addAccountsToList({
        listId,
        accountIds: [accountId],
        credentials: useOAuthStore().token,
      }).then((result) => {
        if (!this.allListsObject[listId]) {
          this.allListsObject[listId] = { accountIds: [] }
        }
        this.allListsObject[listId].accountIds.push(accountId)
        return result
      })
    },
    async removeListAccount({ listId, accountId }) {
      return await removeAccountsFromList({
        listId,
        accountIds: [accountId],
        credentials: useOAuthStore().token,
      }).then((result) => {
        if (!this.allListsObject[listId]) {
          this.allListsObject[listId] = { accountIds: [] }
        }
        const { accountIds } = this.allListsObject[listId]
        const set = new Set(accountIds)
        set.delete(accountId)
        this.allListsObject[listId].accountIds = [...set]

        return result
      })
    },
    async deleteList({ listId }) {
      await deleteList({
        listId,
        credentials: useOAuthStore().token,
      })

      delete this.allListsObject[listId]
      remove(this.allLists, (list) => list.id === listId)
    },
  },
})
